import {Children, ReactElement, ReactNode} from "react";
import {useLocation} from "react-router";

import {Accordion} from "@/components/accordion/Accordion.tsx";
import {useIsSpecialCasePage} from "@/components/navBar/NavBar.utils.tsx";
import {NavBarAccordionItemProps} from "@/components/navBar/subComponents/NavBarAccordion/NavBarAccordionItemProps.types.ts";
import {NavBarItem} from "@/components/navBar/subComponents/NavBarItem/NavBarItem.tsx";
import {NavItemProps} from "@/components/navBar/subComponents/NavBarItem/NavBarItem.types.ts";

// This component is explicit defined here because it is used in the `as` prop of the `Accordion.Header` and it has its own `as` prop to make it a button.
// All other cases that require NavBarItem as a button should use the `NavBarItem` directly with `as={"button"}`.
const NavBarItemButton = ({...props}: Omit<NavItemProps<"button">, "as">) => {
  return <NavBarItem as={"button"} shouldCloseMenu={false} {...props} />;
};

export const NavBarAccordionItem = ({children, name, icon, chip, specialCasePages}: NavBarAccordionItemProps) => {
  const {pathname: currentPage} = useLocation();
  const isSpecialCaseActive = useIsSpecialCasePage(specialCasePages);
  // Check if any child NavBarItem has a `to` prop that includes the current page,
  // ignores all other props such as `href` or `onClick` handlers.
  const isAccordionActive =
    Children.toArray(children).some((child: ReactNode) => {
      const childProps = (child as ReactElement)?.props;
      return childProps?.to === currentPage;
    }) || isSpecialCaseActive;

  return (
    <Accordion.Item eventKey={name}>
      <Accordion.Header
        as={NavBarItemButton}
        icon={icon}
        name={name}
        chip={chip}
        className={isAccordionActive ? "active" : ""}
      />
      <Accordion.Body className={"ms-2"}>
        <ul className={"d-flex flex-column ps-0 mt-2 gap-2"}>{children}</ul>
      </Accordion.Body>
    </Accordion.Item>
  );
};
